import React, { useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import { pick } from 'lodash';
import Button from '@dashboard/components/Button';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import Link from '@dashboard/components/Link';
import usePage from '@dashboard/hooks/usePage';
import { Field } from '@dashboard/components/Formik/Form';
import { toast } from '@dashboard/context/ToastContext';
import useProduct from '../../useProduct';
import { PriceTabsSettings, computeTabsSeed } from './PriceGrouping';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

// Explicit ids: the product form on the same page already owns `title` and `description`.
const FIELDS = [
  {
    name: 'title',
    id: 'pricePickerTitle',
    type: 'text',
    label: 'Heading',
    placeholder: 'Optional heading shown above the prices',
    clearable: true,
    visible: true,
  },
  {
    name: 'description',
    id: 'pricePickerDescription',
    type: 'textarea',
    label: 'Description',
    placeholder: 'Optional description shown under the heading',
    autosize: true,
    minRows: 1,
    clearable: true,
    visible: true,
  },
  { type: 'divider', visible: true },
  {
    name: 'shortenDescription',
    type: 'checkbox',
    label: 'Prices show more / less',
    tooltip: 'Collapse long price descriptions with a Show more link.',
    visible: true,
  },
];

const PRICE_PICKER_FIELDS = [
  'title',
  'description',
  'shortenDescription',
  'priceDisplayLabels',
  'priceDisplayDefaults',
  'priceDisplayTabOrder',
];

function getInitialValues(product, prices, groupingAxis) {
  const picker = product?.pricePicker;
  // `prices` only feeds the price tabs block, which reads it from this form.
  const values = {
    prices,
    title: picker?.title || '',
    description: picker?.description || '',
    shortenDescription: picker?.shortenDescription !== false,
    priceDisplayLabels: picker?.priceDisplayLabels || {},
    priceDisplayDefaults: picker?.priceDisplayDefaults || {},
    priceDisplayTabOrder: picker?.priceDisplayTabOrder || {},
  };
  // Filling in missing tab labels is part of the starting point, not an edit.
  return { ...values, ...computeTabsSeed(values, groupingAxis) };
}

/**
 * The Settings tab of the prices card: the heading and description shown
 * above the options, whether long descriptions are cut behind Show more, and
 * the price tabs. Its own form over the saved product, so nothing outside it
 * changes until Save.
 */
export default function PricesSettingsForm({ groupingAxis, onClose }) {
  const { values: productValues } = useFormikContext();
  const { product, updateProduct } = useProduct();
  const { page } = usePage();
  // Once per mount: a background refetch must not reset what is being typed.
  const [initialValues] = useState(() =>
    getInitialValues(product, productValues.prices || [], groupingAxis),
  );

  async function handleSubmit(values, { resetForm }) {
    try {
      const { error } = await updateProduct.mutateAsync({
        pricePicker: pick(values, PRICE_PICKER_FIELDS),
      });
      if (error) {
        toast({ title: error, type: 'error' });
        return;
      }
      resetForm({ values });
    } catch (err) {
      toast({ title: err?.message || 'Could not save changes', type: 'error' });
    }
  }

  // Not a <form>: this sits inside the product form, and a nested submit
  // would bubble up and save the whole product.
  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, errors, touched, dirty, submitForm }) => (
        <div className="pt-2 mb-6" data-testid="prices-settings">
          <UnsavedChangesReporter />
          <div className="space-y-4">
            <div>
              {FIELDS.map((field, index) => (
                <Field
                  key={field.name || `field-${index}`}
                  field={field}
                  nextFieldType={FIELDS[index + 1]?.type}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
              ))}
            </div>

            <PriceTabsSettings groupingAxis={groupingAxis} />

            {/* Grouping, list/grid and alignment are theme settings - the card's
                "Tabs by …" note comes from there, so say where to change it. */}
            <p className="text-xs text-gray-500 dark:text-muted-foreground">
              You can change the tab and prices layout in the{' '}
              <Link to={`/pages/${page._id}/customize/design/theme#price-options`} color="default">
                Theme
              </Link>{' '}
              tab
            </p>
          </div>

          <div className="mt-4 flex items-center justify-end gap-x-4">
            <FormSubmit.State visible={dirty} />
            <Button type="button" appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" appearance="primary" onClick={() => submitForm()}>
              Save
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
}
